/**
 * Tracks whether this app can be installed to the home screen, and whether the
 * banner offering that should currently be on screen.
 *
 * This lives outside React and runs at import time for one specific reason:
 * Chrome fires `beforeinstallprompt` once, a moment after load, while the
 * visitor is still looking at the sign-in screen. The banner itself only
 * mounts after they are signed in, so a listener registered by that component
 * is always too late and the event is gone for good. Capturing it here, at
 * module scope, means it is already waiting by the time anything renders.
 *
 * Dismissal is remembered for the browsing session only. It used to be
 * permanent, which meant one stray tap on the X and the prompt was never
 * offered again on that device -- indistinguishable, from the outside, from
 * the feature being broken.
 */

/**
 * The same key in two storages: it now lives in sessionStorage, and the
 * localStorage copy is the old permanent flag, cleared on sight.
 */
const DISMISSED_KEY = 'squares:install-dismissed';

/** The event is still non-standard, so it is typed here rather than imported. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallState {
  /** Chrome-style prompt is held and ready to replay. */
  deferred: BeforeInstallPromptEvent | null;
  /** iOS Safari, where the only route is Share -> Add to Home Screen. */
  iosHint: boolean;
  /** Already running from the home screen, or installed during this visit. */
  installed: boolean;
  /** Dismissed for this session. */
  dismissed: boolean;
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari's own flag, set when launched from the home screen.
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function isIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  const iOS =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports itself as a Mac; the touch point count gives it away.
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  // Chrome and Firefox on iOS are Safari underneath but cannot install.
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return iOS && isSafari;
}

function readDismissed(): boolean {
  try {
    // A permanent dismissal from the previous build would otherwise silence
    // the banner forever, so it is cleared rather than migrated.
    localStorage.removeItem(DISMISSED_KEY);
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    // Private mode or blocked storage: just show it.
    return false;
  }
}

const state: InstallState = {
  deferred: null,
  iosHint: false,
  installed: false,
  dismissed: false,
};

const listeners = new Set<(s: InstallState) => void>();

function emit() {
  for (const fn of listeners) fn({ ...state });
}

export function subscribeToInstallState(fn: (s: InstallState) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getInstallState(): InstallState {
  return { ...state };
}

/** Hides the banner for the rest of this browsing session. */
export function dismissInstallPrompt() {
  state.dismissed = true;
  try {
    sessionStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    /* nothing to remember it with; it reappears on the next view */
  }
  emit();
}

/** Replays Chrome's held prompt. Resolves to what the visitor chose. */
export async function runInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = state.deferred;
  if (!event) return 'unavailable';
  // The held event is single-use, so it is dropped whatever the outcome.
  state.deferred = null;
  await event.prompt();
  const { outcome } = await event.userChoice;
  if (outcome === 'accepted') state.installed = true;
  emit();
  return outcome;
}

if (typeof window !== 'undefined') {
  state.installed = isStandalone();
  state.dismissed = readDismissed();
  state.iosHint = isIosSafari() && !state.installed;

  window.addEventListener('beforeinstallprompt', (event) => {
    // Suppress Chrome's own mini-infobar so ours is the only prompt.
    event.preventDefault();
    state.deferred = event as BeforeInstallPromptEvent;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    state.deferred = null;
    state.installed = true;
    emit();
  });

  // Covers installing from the browser menu while the tab stays open.
  window.matchMedia('(display-mode: standalone)').addEventListener('change', (e) => {
    if (e.matches) {
      state.installed = true;
      emit();
    }
  });
}
